/**
 * UI strings, per locale (docs/I18N.md). Each module owns one dictionary file with the English source (`en`) and the
 * Vietnamese `vi` (a complete Record of the module's `en` keys, so a missing key is a type error). Other locales live in
 * per-locale folders (`zh-Hans/<module>.json`, typed in `zh-Hans/index.ts` as a complete `Record<TKey, string>` for
 * available locales, `Partial` for later waves) and are loaded lazily by `loadLocale()` together with the content
 * catalogs. Lookup walks the locale's fallback chain (core `fallbackChain`). Messages may use `{name}` placeholders
 * and ICU-like plurals: `{n, plural, one {# drawing} other {# drawings}}`. `npm run i18n:check` reports missing /
 * unused keys per locale. Keys are prefixed per module to avoid clashes:
 *   common.ts      no prefix       shared chrome (nav, buttons, errors)
 *   onboarding.ts  onb.* acc.* pay.* set.* dbg.*   landing, onboarding, account, paywall, settings, cheats
 *   lesson.ts      les.* cap.* fb.* cel.* dt.* stm.*   lesson player, capture, feedback, celebrations, draw together, share
 *   assets.ts      ast.*   self-made animation set (loading lines, intro clip, update, ask-to-buy)
 *   features.ts    learn.* pg.* jr.* st.* tale.* wc.* mail.* prog.*   main tabs and retention features
 *   art.ts         art.*          art system: construction stages, lesson intro hero, world maps, art-style switch
 *   services.ts    svc.*          sign-in providers, purchases (Ask to Buy), push, app update, managed devices
 *   collect.ts     col.* hab.*    WOW collections (creature book, sticker album, gallery wall) + daily habit
 *   colour.ts      cw.* cc.*      WOW colour the world (map bloom) + stage-5 colouring coach
 *   living.ts      liv.*          WOW living drawings + the first-2-minutes drawing
 *   feel.ts        fx.* par.* msr.*   WOW Juno reactions + sound, grown-ups corner, funnel dashboard
 *   money.ts       gate.* wish.* mon.* inv.* gift.* skin.*   parental gate, wishes, paywall, plan, invites, gifts, book style
 *   v2.ts          v2.*           curriculum v2: new lesson items, scaffold hint, map chapters / finale / crossroads, playground levels
 *   story.ts       sty.*          story layer: meet sheets, Character Cards, narration, reactions, comics, storybook, bedtime
 *   canvas.ts      cv.*           Draw Studio: on-screen drawing canvas (tools, sizes, lesson palette, undo / redo, helpers)
 *   observe.ts     obs.*          draw from real objects: shape overlay, treasure hunt, Object Museum
 * Placeholders use {name}.
 */
import { useCallback } from 'react';
import { create } from 'zustand';
import { fill, fallbackChain, formatPlurals, localize, type L, type Lang, type Locale } from '@sketchling/core';
import { loadContentCatalogs } from '../content';
import { useApp } from '../../state/store';

/** Bumps whenever a locale's dictionaries / catalogs arrive, so every t() / loc() consumer re-renders. */
export const useI18nRev = create<{ rev: number }>(() => ({ rev: 0 }));
import * as common from './common';
import * as onboarding from './onboarding';
import * as lesson from './lesson';
import * as features from './features';
import * as services from './services';
import * as assets from './assets';
import * as parity from './parity';
import * as art from './art';
import * as collect from './collect';
import * as colour from './colour';
import * as living from './living';
import * as feel from './feel';
import * as money from './money';
import * as v2 from './v2';
import * as canvas from './canvas';
import * as story from './story';
import * as observe from './observe';

/** Every UI module (tooling: i18n:check / extraction walk this list). */
export const MODULES = { common, onboarding, lesson, features, assets, services, parity, art, collect, colour, living, feel, money, v2, canvas, story, observe };

const en = { ...common.en, ...onboarding.en, ...lesson.en, ...features.en, ...assets.en, ...services.en, ...parity.en, ...art.en, ...collect.en, ...colour.en, ...living.en, ...feel.en, ...money.en, ...v2.en, ...canvas.en, ...story.en, ...observe.en };
const vi: Record<keyof typeof en, string> = { ...common.vi, ...onboarding.vi, ...lesson.vi, ...features.vi, ...assets.vi, ...services.vi, ...parity.vi, ...art.vi, ...collect.vi, ...colour.vi, ...living.vi, ...feel.vi, ...money.vi, ...v2.vi, ...canvas.vi, ...story.vi, ...observe.vi };

export type TKey = keyof typeof en;
export type Dict = Record<TKey, string>;
/** Loaded dictionaries (en + vi are bundled; other locales arrive through loadLocale). */
export const DICTS: Partial<Record<Locale, Partial<Dict>>> = { en, vi };

/** Lazy per-locale UI dictionaries (one chunk each). */
const UI_LOADERS: Partial<Record<Locale, () => Promise<Partial<Dict>>>> = {
  'zh-Hans': () => import('./zh-Hans/index').then((m) => m.dict),
};
const uiLoaded = new Map<Locale, Promise<void>>();

/** True when the UI dictionary of every locale in the chain is in memory. */
export const isLocaleLoaded = (l: Locale) => fallbackChain(l).every((x) => !!DICTS[x] || !UI_LOADERS[x]);

/** Load the UI dictionary + content catalog of a locale (and its fallbacks). Idempotent. */
export function loadLocale(l: Locale): Promise<void> {
  const ui = fallbackChain(l).map((x) => {
    const load = UI_LOADERS[x];
    if (!load || DICTS[x]) return undefined;
    let p = uiLoaded.get(x);
    if (!p) {
      p = load().then((d) => {
        DICTS[x] = d;
      });
      uiLoaded.set(x, p);
    }
    return p;
  });
  return Promise.all([...ui, loadContentCatalogs(l)]).then(() => {
    useI18nRev.setState((s) => ({ rev: s.rev + 1 }));
  });
}

/** Raw message for a key along the fallback chain (no placeholders filled). */
export function message(lang: Lang, key: TKey): string {
  for (const l of fallbackChain(lang)) {
    const s = DICTS[l]?.[key];
    if (s) return s;
  }
  return en[key] ?? key;
}

export function translate(lang: Lang, key: TKey, vars?: Record<string, string | number>): string {
  const s = message(lang, key);
  return vars ? fill(formatPlurals(s, vars, lang), vars) : s;
}

/** `const t = useT(); t('continue')`. */
export function useT() {
  const lang = useApp((s) => s.settings.language);
  const rev = useI18nRev((s) => s.rev);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((key: TKey, vars?: Record<string, string | number>) => translate(lang, key, vars), [lang, rev]);
}

/** Localise content text (a locale record) in the current language. */
export function useLoc() {
  const lang = useApp((s) => s.settings.language);
  const rev = useI18nRev((s) => s.rev);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback((text: L | string, vars?: Record<string, string | number>) => (vars ? fill(localize(text, lang), vars) : localize(text, lang)), [lang, rev]);
}

export const useLang = () => useApp((s) => s.settings.language);
